import { Injectable, inject } from '@angular/core';

import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { OddsDisplayFormat } from '@frontend/sports/types/models/user-settings';
import { UserOddsFormatSettingsService } from '@frontend/sports/user/feature';
import { Decimal } from 'decimal.js';

import { NativePrice } from './native-price';

@Injectable({ providedIn: 'root' })
export class NativePriceFormatterService {
    private CUT_OFF_DECIMAL_PLACES_LIMIT = 1.01;
    private readonly DECIMAL_PLACES = 2;

    private betslipConfig = inject(BetslipConfig);
    private readonly userOddsFormatSettingsService = inject(UserOddsFormatSettingsService);

    format(
        price: NativePrice | string | number | null | undefined,
        cutOff: boolean = false,
        cutZero: boolean = false,
        maxDecimalPlaces: number = 3,
    ): string {
        if (!price) {
            return this.formatInvalidOdds();
        }

        if (typeof price === 'string') {
            return price;
        }

        if (typeof price === 'number') {
            price = NativePrice.fromDecimals(price);
        }

        if (price.isEmpty()) {
            return this.formatInvalidOdds();
        }

        return this.getFormattedOdds(price, cutOff, cutZero, maxDecimalPlaces);
    }

    private getFormattedOdds(price: NativePrice, cutOff: boolean = false, cutZero: boolean = false, maxDecimalPlaces = 3): string {
        switch (this.userOddsFormatSettingsService.oddsFormat) {
            case OddsDisplayFormat.UK:
                return price.numerator.greaterThan(0) && price.denominator.greaterThan(0)
                    ? this.toUKOdds(price, maxDecimalPlaces)
                    : this.formatInvalidOdds();
            case OddsDisplayFormat.US:
                return this.toUSOdds(price);
            default:
                return this.toEUOdds(price, cutOff, cutZero, maxDecimalPlaces);
        }
    }

    private toUSOdds(price: NativePrice): string {
        if (price.americanOdds.equals(0)) {
            return this.formatInvalidOdds();
        }

        const usOddsString = price.americanOdds.toString();

        return price.americanOdds.greaterThan(0) ? '+' + usOddsString : usOddsString;
    }

    private toEUOdds(price: NativePrice, cutOff: boolean, cutZero: boolean, maxDecimalPlaces = 3): string {
        const formatPrice = cutOff ? NativePrice.fromDecimals(this.cutOff(price.odds).toNumber()) : price;

        // calculated values ending with multiple 0 decimals
        if (cutZero && formatPrice.odds.decimalPlaces() > this.DECIMAL_PLACES) {
            const result = formatPrice.odds.toFixed(
                formatPrice.odds.decimalPlaces() > this.DECIMAL_PLACES ? maxDecimalPlaces : this.DECIMAL_PLACES,
                Decimal.ROUND_DOWN,
            );

            return result.endsWith('0') ? formatPrice.odds.toFixed(this.DECIMAL_PLACES, Decimal.ROUND_DOWN) : result;
        }

        return formatPrice.odds.toFixed(
            formatPrice.odds.decimalPlaces() > this.DECIMAL_PLACES ? maxDecimalPlaces : this.DECIMAL_PLACES,
            Decimal.ROUND_DOWN,
        );
    }

    private toUKOdds(price: NativePrice, maxDecimalPlaces = 3): string {
        const numerator = price.numerator.toFixed(Math.min(price.numerator.decimalPlaces(), maxDecimalPlaces) || 0, Decimal.ROUND_DOWN);

        return `${numerator}/${price.denominator.toNumber()}`;
    }

    private cutOff(value: Decimal, maxDecimalPlaces = 3): Decimal {
        const decimalPlaces = value.lessThan(this.CUT_OFF_DECIMAL_PLACES_LIMIT) ? maxDecimalPlaces : this.DECIMAL_PLACES;

        return this.betslipConfig.roundValues ? value.toDecimalPlaces(decimalPlaces) : value.toDecimalPlaces(decimalPlaces, Decimal.ROUND_UP);
    }

    private formatInvalidOdds(): string {
        switch (this.userOddsFormatSettingsService.oddsFormat) {
            case OddsDisplayFormat.US:
                return '---';
            case OddsDisplayFormat.UK:
                return '-/-';
            default:
                return '-.--';
        }
    }
}
